import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Copy, 
  Check, 
  Download, 
  FileText, 
  Code,
  BarChart3,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { GeneratedCode } from '@/types/codeInjection';
import { formatCodeSize } from '@/utils/codeInjectionGenerator';
import { useRestaurantBranding } from '@/hooks/useRestaurantBranding';

interface CodeOutputProps {
  generatedCode: GeneratedCode;
  isGenerating?: boolean;
}

/**
 * CodeOutput - Display generated code with syntax highlighting and copy functionality
 */
const CodeOutput: React.FC<CodeOutputProps> = ({ 
  generatedCode, 
  isGenerating = false 
}) => {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const headCodeRef = useRef<HTMLPreElement>(null);
  const bodyCodeRef = useRef<HTMLPreElement>(null);
  const { activeRestaurant } = useRestaurantBranding();

  const { headCode, bodyCode, stats } = generatedCode;

  const handleCopy = async (code: string, section: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleDownload = (code: string, filename: string) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isEmpty = !headCode && !bodyCode;

  if (isEmpty) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Generated Code
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">Select components to generate code</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Code Output */}
      {isEmpty ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Generated Code
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">Select components to generate code</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="head" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="head" className="flex items-center gap-2 data-[state=active]:text-white group">
              <FileText className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              Head Code
              <Badge variant="outline" className="ml-1 group-data-[state=active]:bg-brand-yellow group-data-[state=active]:text-black group-data-[state=active]:border-brand-yellow">
                {formatCodeSize(stats.headSize)}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="body" className="flex items-center gap-2 data-[state=active]:text-white group">
              <Code className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              Body Code
              <Badge variant="outline" className="ml-1 group-data-[state=active]:bg-brand-yellow group-data-[state=active]:text-black group-data-[state=active]:border-brand-yellow">
                {formatCodeSize(stats.bodySize)}
              </Badge>
            </TabsTrigger>
          </TabsList>
          
          <Card className="bg-gray-50">
            <CardContent className="bg-gray-50">
              <TabsContent value="head" className="mt-4">
                <div className="space-y-3">
                  <ScrollArea className="h-80 w-full rounded-md border bg-white">
                    <pre 
                      ref={headCodeRef}
                      className="p-4 text-sm font-mono whitespace-pre-wrap"
                      style={{ tabSize: 2 }}
                    >
                      <code className="language-html">{headCode}</code>
                    </pre>
                  </ScrollArea>
                  
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(headCode, 'head')}
                      className="flex items-center gap-1"
                    >
                      {copiedSection === 'head' ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(headCode, 'head-injection.html')}
                      className="flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="body" className="mt-4">
                <div className="space-y-3">
                  <ScrollArea className="h-80 w-full rounded-md border bg-white">
                    <pre 
                      ref={bodyCodeRef}
                      className="p-4 text-sm font-mono whitespace-pre-wrap"
                      style={{ tabSize: 2 }}
                    >
                      <code className="language-html">{bodyCode}</code>
                    </pre>
                  </ScrollArea>
                  
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(bodyCode, 'body')}
                      className="flex items-center gap-1"
                    >
                      {copiedSection === 'body' ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(bodyCode, 'body-injection.html')}
                      className="flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      )}

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Usage Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-semibold">
                1
              </div>
              <div className="flex-1">
                <span className="font-medium">Navigate to your Pump'd admin dashboard here:</span>
              </div>
            </div>
            <div className="ml-10">
              <Button asChild size="sm">
                <a 
                  href={activeRestaurant?.id 
                    ? `https://admin.pumpd.co.nz/restaurant/${activeRestaurant.id}/settings` 
                    : 'https://admin.pumpd.co.nz/'
                  } 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Admin Dashboard
                </a>
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-semibold">
                2
              </div>
              <div className="flex-1">
                <span className="font-medium">Navigate to Settings → Website → Additional Settings → Custom CSS and JavaScript</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-semibold">
                3
              </div>
              <div className="flex-1">
                <span className="font-medium">One at a time, copy the generated Head and Body code injections into the text fields</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-semibold">
                4
              </div>
              <div className="flex-1">
                <span className="font-medium">After clicking "Save", visit your ordering page to see the changes:</span>
              </div>
            </div>
            {activeRestaurant?.ordering_page_url && (
              <div className="ml-10">
                <Button asChild size="sm" variant="outline">
                  <a 
                    href={activeRestaurant.ordering_page_url.startsWith('http') 
                      ? activeRestaurant.ordering_page_url 
                      : `https://${activeRestaurant.ordering_page_url}`
                    } 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Ordering Page
                  </a>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default CodeOutput;